import React from "react";
import { 
    CheckCircleFilled, 
    CloseCircleFilled, 
    ExclamationCircleFilled, 
    InfoCircleFilled 
} from "@ant-design/icons";
import { Tooltip } from "antd";

interface ToastProps {
    message: string;
    mode: "success" | "error" | "warning" | "info";
    onClose: () => void;
}

const TOAST_CONFIG = {
    success: { icon: <CheckCircleFilled className="icon-success" />, className: "toast-success" },
    error: { icon: <CloseCircleFilled className="icon-error" />, className: "toast-error" },
    info: { icon: <InfoCircleFilled className="icon-info" />, className: "toast-info" },
    warning: { icon: <ExclamationCircleFilled className="icon-warning" />, className: "toast-warning" },
};

export const Toast: React.FC<ToastProps> = ({ mode, message, onClose }) => {
    const config = TOAST_CONFIG[mode] || TOAST_CONFIG.info;

    return (
        <div className={`toast-container ${config.className}`}>
            <div className="toast-content">
                <span className="toast-icon-wrapper">{config.icon}</span>
                <div className="toast-text-area">
                    <Tooltip title={message} placement="topLeft">
                        <span className="toast-message">{message}</span>
                    </Tooltip>
                </div>
            </div>
            <button className="toast-close-btn" onClick={onClose} aria-label="Close">
                &times;
            </button>

            <style dangerouslySetInnerHTML={{
                __html: `
                .toast-container {
                  position: fixed;
                  bottom: 24px;
                  right: 24px;
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding: 12px 16px;
                  border-radius: 12px;
                  box-shadow:
                    0 10px 15px -3px rgba(0, 0, 0, 0.1),
                    0 4px 6px -2px rgba(0, 0, 0, 0.05);
                  width: auto;
                  min-width: 280px;
                  max-width: 400px;
                  z-index: 1010;
                  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                  border: 1px solid rgba(0, 0, 0, 0.05);
                }

                .toast-content {
                  display: flex;
                  align-items: flex-start;
                  gap: 12px;
                  flex: 1;
                  overflow: hidden;
                }

                .toast-icon-wrapper {
                  font-size: 1.1rem;
                  flex-shrink: 0;
                }

                .toast-text-area {
                  flex: 1;
                  overflow: hidden;
                }

                .toast-message {
                  display: block;
                  font-size: 14px;
                  line-height: 1.5;
                  color: #1f2937;
                  word-wrap: break-word;
                  overflow-wrap: break-word;
                  display: -webkit-box;
                  -webkit-line-clamp: 3;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                }

                .toast-close-btn {
                  background: transparent;
                  border: none;
                  padding: 0 0 0 12px;
                  margin-top: -2px;
                  font-size: 20px;
                  line-height: 1;
                  cursor: pointer;
                  color: #9ca3af;
                  flex-shrink: 0;
                  transition: color 0.2s;
                }

                .toast-close-btn:hover {
                  color: #4b5563;
                }

                .toast-success {
                  background-color: #f0fdf4;
                  border-left: 6px solid #22c55e;
                }

                .toast-error {
                  background-color: #fef2f2;
                  border-left: 6px solid #ef4444;
                }

                .toast-info {
                  background-color: #f5f3ff;
                  border-left: 6px solid #8b5cf6;
                }

                .toast-warning {
                  background-color: #fffbeb;
                  border-left: 6px solid #f59e0b;
                }

                .icon-success { color: #22c55e; }
                .icon-error { color: #ef4444; }
                .icon-info { color: #8b5cf6; }
                .icon-warning { color: #f59e0b; }

                @media (max-width: 480px) {
                  .toast-container {
                    right: 5%;
                    left: 5%;
                    bottom: 16px;
                    width: 90%;
                    max-width: none;
                    min-width: 0;
                    z-index: 1010;
                  }
                  .toast-message {
                    font-size: 13px;
                  }
                }
            `}} />
        </div>
    );
};